/* eslint-disable import/prefer-default-export, consistent-return */
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { translate } from 'translations';
import { selectIsLeanUser, selectToken, selectTenantName } from '@redux/slices/userSlice';
import { ContentCardProps, Divider, Loader } from 'learning-panel-components';
import CardsGalleryContainer from '@components/cardsGallery/cardsGalleryContainer';
import { useOtherMembersTaken } from '@queries/core/homepage';
import convertCourseToContentCard from '@services/convertCourseToContentCard';
import convertLessonToContentCard from '@services/convertLessonToContentCard';
import convertLearningPathToContentCard from '@services/convertLearningPathToContentCard';
import convertSkillTreeToContentCard from '@services/convertSkillTreeToContentCard';
import convertVideosToContentCard from '@services/convertVideosToContentCard';
import convertCourseToBanner from '@services/convertCourseToBanner';
import CourseBanner from '@components/learn/courseBanner/courseBanner';
import LessonBanner from '@components/learn/lessonBanner/lessonBanner';
import convertLessonToBanner from '@services/convertLessonToBanner';
import { progressOrder, filterHiddenLearningPaths } from '@components/learningPath/utils';
import styles from './othersMembersTaking.module.scss';

const OthersMembersTakingContainer: FC<OtherMembersTakingProps> = (props: OtherMembersTakingProps) => {
    const { setCertificateCourseUuid, setIsCustomCourse, setVideoTutorialUuid, onContactUsClick, fullWidth } = props;
    const token = useSelector(selectToken);
    const isLeanUser = useSelector(selectIsLeanUser);
    const tenantName = useSelector(selectTenantName);
    const [myTeamTaken, setMyTeamTaken] = useState<ContentCardProps[]>([]);
    const [myOrgnizationTaken, setMyOrginizztionTaken] = useState<ContentCardProps[]>([]);
    const [mostPopular, setMostPopular] = useState<any>();
    const [fetchingData, setFetchingData] = useState<boolean>(true);
    const { data: otherMembersTakenData, isLoading: otherMembersTakenLoading } = useOtherMembersTaken(token);

    useEffect(() => {
        if (!otherMembersTakenData) return;
        if ((otherMembersTakenData as any).success === false) return;
        setFetchingData(true);
        const myTenantData = otherMembersTakenData.tenant;
        const myTeamData = otherMembersTakenData.team;
        const popularContentData = otherMembersTakenData.popularContent;
        const tempCoursesOrg = mapToContentCards(
            myTenantData.courses,
            convertCourseToContentCard,
            isLeanUser,
            setCertificateCourseUuid,
            setIsCustomCourse
        );

        const tempLessonsOrg = mapToContentCards(
            myTenantData.lessons,
            convertLessonToContentCard,
            isLeanUser,
            onContactUsClick
        );

        const tempSkillTreeOrg = mapToContentCards(myTenantData.skillTrees, convertSkillTreeToContentCard);
        const tempLearningPathsOrg = mapToContentCards(
            filterHiddenLearningPaths(myTenantData.learningPaths, tenantName),
            convertLearningPathToContentCard
        );
        const tempVideoOrg = mapToContentCards(
            myTenantData.videoTutorials,
            convertVideosToContentCard,
            setVideoTutorialUuid
        );

        const tempCoursesTeam = mapToContentCards(
            myTeamData.courses,
            convertCourseToContentCard,
            isLeanUser,
            setCertificateCourseUuid,
            setIsCustomCourse
        );
        const tempLessonsTeam = mapToContentCards(
            myTeamData.lessons,
            convertLessonToContentCard,
            isLeanUser,
            onContactUsClick
        );
        const tempSkillTreeTeam = mapToContentCards(myTeamData.skillTrees, convertSkillTreeToContentCard);
        const tempLearningPathTeam = mapToContentCards(
            filterHiddenLearningPaths(myTeamData.learningPaths, tenantName),
            convertLearningPathToContentCard
        );
        const tempVideoTeam = mapToContentCards(
            myTeamData.videoTutorials,
            convertVideosToContentCard,
            setVideoTutorialUuid
        );

        const myTeamTaken = [...tempCoursesTeam, ...tempLessonsTeam, ...tempVideoTeam, ...tempSkillTreeTeam].sort(
            (a, b) => {
                const indexA = progressOrder.indexOf(a.status);
                const indexB = progressOrder.indexOf(b.status);
                return indexA - indexB;
            }
        );
        setMyTeamTaken(myTeamTaken);

        const myOrgTaken = [
            ...tempCoursesOrg,
            ...tempLessonsOrg,
            ...tempVideoOrg,
            ...tempSkillTreeOrg,
            ...tempLearningPathsOrg
        ].sort((a, b) => {
            const indexA = progressOrder.indexOf(a.status);
            const indexB = progressOrder.indexOf(b.status);
            return indexA - indexB;
        });
        setMyOrginizztionTaken(myOrgTaken);

        if (popularContentData.course) {
            setMostPopular({ contentType: 'course', content: popularContentData.course });
        } else if (popularContentData.lesson) {
            setMostPopular({ contentType: 'lesson', content: popularContentData.lesson });
        }
        setFetchingData(false);
    }, [otherMembersTakenData, tenantName]);

    const mapToContentCards = (items: any[], contentCardConverter: Function, ...args: any[]) => {
        if (items === undefined) {
            return [];
        }
        return items.map(item => contentCardConverter(item, ...args));
    };

    const getBanner = () => {
        if (mostPopular?.contentType === 'course') {
            const courseBanner = convertCourseToBanner(mostPopular.content);
            return <CourseBanner {...courseBanner} />;
        }

        if (mostPopular?.contentType === 'lesson') {
            const lessonBanner = convertLessonToBanner(mostPopular.content);
            return <LessonBanner {...lessonBanner} />;
        }
    };

    return (
        <div id="career-page" className={styles['other-member-taken-wrapper']}>
            {fetchingData ? (
                <Loader showBackground={false} />
            ) : (
                <>
                    {mostPopular && getBanner()}
                    <div className={fullWidth ? styles.container : styles['no-full-container']}>
                        {myTeamTaken.length > 0 && (
                            <CardsGalleryContainer
                                rows={2}
                                columns={2}
                                noFullWidthColumn={3}
                                data={myTeamTaken}
                                title={translate('MyTeamIsTaking')}
                                fullWidth={fullWidth}
                                infoDescription={translate('MostPopularContentWithinYourTeam')}
                            />
                        )}
                        {myOrgnizationTaken.length > 0 && myTeamTaken.length > 0 && (
                            <div className={styles.space}></div>
                        )}
                        {myOrgnizationTaken.length > 0 && (
                            <CardsGalleryContainer
                                rows={2}
                                columns={myTeamTaken.length > 0 ? 2 : 4}
                                noFullWidthColumn={3}
                                data={myOrgnizationTaken}
                                title={translate('MyOrganizationIsTaking')}
                                fullWidth={fullWidth}
                                infoDescription={translate('MostPopularContentWithinYourOrg')}
                            />
                        )}
                        {myOrgnizationTaken.length === 0 && myTeamTaken.length === 0 && !fetchingData && (
                            <div className={styles['empty-state']}>
                                <div className={styles['title-no-assigned']}>
                                    {translate('BeTheFirstToBashVulnerabilities')}
                                </div>
                                <Divider
                                    className={styles.divider}
                                    orientation="horizontal"
                                    color="rgba(255, 255, 255, 0.2)"
                                />
                                <div className={styles.description}>{translate('noOneTakeContentMessage')}</div>
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/assignedContentEmpty.svg`}
                                    alt="noAssignedContent"
                                ></img>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

interface OtherMembersTakingProps {
    setCertificateCourseUuid: Function;
    setIsCustomCourse: Function;
    setVideoTutorialUuid: Function;
    onContactUsClick: Function;
    fullWidth?: boolean;
}

export default OthersMembersTakingContainer;
